import { useRef, useState } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { entity, errorMessage } from '@/lib/db'

export default function PhotoUploader({
  onUploaded,
  projects = [],
}: {
  onUploaded: () => void
  projects?: string[]
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [group, setGroup] = useState('')

  const groupName = group.trim() || `Project ${projects.length + 1}`

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList).filter(file => file.type.startsWith('image/'))
    if (files.length === 0) {
      setError('Please choose image files.')
      return
    }

    setBusy(true)
    setError('')
    setStatus(`Uploading ${files.length} photo${files.length > 1 ? 's' : ''}...`)

    try {
      const results = await lumi.tools.file.upload(files)
      const saved = results.filter(result => result.fileUrl)

      for (let i = 0; i < saved.length; i += 1) {
        const result = saved[i]
        await entity('portfolio_photos').create({
          imageUrl: result.fileUrl,
          fileName: result.fileName,
          alt: result.fileName.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '),
          project: groupName,
          projectTitle: groupName,
          order: Date.now() + i,
        })
      }
      setGroup('')

      const failed = results.length - saved.length
      setStatus(
        failed > 0
          ? `${saved.length} uploaded, ${failed} skipped.`
          : `${saved.length} photo${saved.length > 1 ? 's' : ''} added.`,
      )
      onUploaded()
    } catch (err) {
      setError(errorMessage(err, 'Upload failed, please try again.'))
      setStatus('')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-dashed border-forest-950/20 bg-bone-50/70 p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
      <div>
        <p className="text-sm font-semibold text-forest-950">Add photos to a project card</p>
        <p className="mt-1 text-xs leading-relaxed text-forest-950/55">
          Photos uploaded together become one slider card. Choose several at once so the card
          has multiple slides.
        </p>
        <label className="mt-3 block text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-950/45">
          Project card
        </label>
        <input
          type="text"
          value={group}
          onChange={event => setGroup(event.target.value)}
          list="portfolio-project-groups"
          placeholder={groupName}
          className="mt-1 w-full max-w-xs rounded-full border border-bone-300 bg-bone-50 px-4 py-2 text-sm text-forest-950 placeholder:text-forest-950/35 focus-visible:border-pine focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine/30"
        />
        <datalist id="portfolio-project-groups">
          {projects.map(name => (
            <option key={name} value={name} />
          ))}
        </datalist>
        {(status || error) && (
          <p
            role="status"
            className={`mt-2 text-xs font-medium ${error ? 'text-red-700' : 'text-forest-950/60'}`}
          >
            {error || status}
          </p>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={event => handleFiles(event.target.files)}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="mt-3 inline-flex shrink-0 items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-sm font-semibold text-bone-50 transition hover:bg-pine-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine focus-visible:ring-offset-2 focus-visible:ring-offset-bone-50 disabled:opacity-60 sm:mt-0"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Upload className="h-4 w-4" aria-hidden="true" />
        )}
        {busy ? 'Uploading...' : 'Upload photos'}
      </button>
    </div>
  )
}
